import MomentUtils from '@date-io/moment';
import { Avatar, Chip, createStyles, makeStyles, Theme } from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { Moment } from 'moment';
import React from 'react';

import { spacings, TextSizesEnum } from '../../../../theme';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { FactSearch } from '../create_plex/form_components/FactSearch';
import { NewCollaboratorSearch } from '../create_plex/form_components/NewCollaboratorSearch';
import { CreateOrEditDialogTitle } from '../post/CreateOrEditDialogTitle';

type FormData = {
  selectedFact?: {
    id: string;
    title: string;
    permalink: string;
  };
  authors: { id: string; picture: string; name: string }[];
  reviewers: { id: string; picture: string; name: string }[];
  reviewableBy: Moment | null;
  reviewBy: Moment | null;
};

type Props = {
  mode: 'create' | 'update';
  data: FormData;
  onChange: (data: FormData) => void;
};

export function ResourceReviewProcessDialogContent({ mode, data, onChange }: Props): React.ReactElement {
  const classes = useStyles();

  return (
    <div className={classes.content}>
      <CreateOrEditDialogTitle />

      <div className={classes.formControl}>
        <PlexTypography size={TextSizesEnum.Large}>Resource</PlexTypography>

        <div className={classes.factFormControl}>
          {data.selectedFact != null && (
            <Chip
              label={data.selectedFact.title}
              component="a"
              target="_blank"
              rel="noopener noreferrer"
              href={data.selectedFact.permalink}
              clickable
              variant="outlined"
              onDelete={
                mode === 'create'
                  ? (event: React.SyntheticEvent<unknown>) => {
                      // Do not activate the link
                      event.preventDefault();
                      onChange({ ...data, selectedFact: undefined });
                    }
                  : undefined
              }
            />
          )}

          {data.selectedFact == null && (
            <FactSearch
              onSelect={(option) => {
                onChange({
                  ...data,
                  selectedFact: {
                    id: option.id,
                    title: option.body.text || '',
                    permalink: option.body.permalink,
                  },
                });
              }}
            />
          )}
        </div>
      </div>

      <MultiUserFormControl
        label="Authors"
        users={data.authors}
        onAdd={(newUser) => {
          onChange({
            ...data,
            authors: [...data.authors.filter((user) => user.id !== newUser.id), newUser],
          });
        }}
        onDelete={(userId) => {
          onChange({
            ...data,
            authors: data.authors.filter((user) => user.id !== userId),
          });
        }}
      />

      <MultiUserFormControl
        label="Reviewers"
        users={data.reviewers}
        onAdd={(newUser) => {
          onChange({
            ...data,
            reviewers: [...data.reviewers.filter((user) => user.id !== newUser.id), newUser],
          });
        }}
        onDelete={(userId) => {
          onChange({
            ...data,
            reviewers: data.reviewers.filter((user) => user.id !== userId),
          });
        }}
      />

      <div className={classes.formControl}>
        <PlexTypography size={TextSizesEnum.Large}>Reviewable By</PlexTypography>

        <MuiPickersUtilsProvider utils={MomentUtils}>
          <KeyboardDatePicker
            autoOk
            variant="inline"
            format="YYYY/MM/DD"
            margin="dense"
            label="Date"
            value={data.reviewableBy}
            onChange={(date) => {
              onChange({ ...data, reviewableBy: date });
            }}
            KeyboardButtonProps={{
              'aria-label': 'change reviewable by date',
            }}
          />
        </MuiPickersUtilsProvider>
      </div>

      <div className={classes.formControl}>
        <PlexTypography size={TextSizesEnum.Large}>Review By</PlexTypography>

        <MuiPickersUtilsProvider utils={MomentUtils}>
          <KeyboardDatePicker
            autoOk
            variant="inline"
            format="YYYY/MM/DD"
            margin="dense"
            label="Date"
            value={data.reviewBy}
            onChange={(date) => {
              onChange({ ...data, reviewBy: date });
            }}
            KeyboardButtonProps={{
              'aria-label': 'change review by date',
            }}
          />
        </MuiPickersUtilsProvider>
      </div>
    </div>
  );
}

type UserFragment = { id: string; picture: string; name: string };

const MultiUserFormControl = ({
  label,
  users,
  onAdd,
  onDelete,
}: {
  label: string;
  users: UserFragment[];
  onAdd: (user: UserFragment) => void;
  onDelete: (id: string) => void;
}) => {
  const classes = useStyles();

  return (
    <div className={classes.formControl}>
      <PlexTypography size={TextSizesEnum.Large}>{label}</PlexTypography>

      <div>
        <div className={classes.authorChips}>
          {users.map((user) => (
            <Chip
              avatar={<Avatar alt={user.name} src={user.picture} />}
              label={user.name}
              onDelete={() => {
                onDelete(user.id);
              }}
            />
          ))}
        </div>

        <NewCollaboratorSearch
          onAdd={(collaborator) => {
            if (collaborator.plexUserId != null) {
              onAdd({
                id: collaborator.plexUserId,
                name: collaborator.name || '',
                picture: collaborator.picture || '',
              });
            } else {
              throw new Error(`Invalid response ${JSON.stringify(collaborator)}`);
            }
          }}
          existingMembers={[]}
          onlySignedUpUsers
        />
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    content: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
    },
    formControl: {
      margin: spacings.normal,
    },
    factFormControl: {
      height: '64px',
      display: 'flex',
      alignItems: 'center',
    },
    authorChips: {
      margin: `${spacings.small}px 0`,
      '& > * + *': {
        marginLeft: spacings.xsmall,
      },
    },
  })
);
